<script setup>
/**
 * Config input for the `send_whatsapp_template` automation action.
 * Plan: docs/smarticks/whatsapp-template-automation-plan.md
 *
 * Two groups, one divider: "what we send" (channel / template / fields / preview) and
 * "when and how often" (delay / suppression). Without the divider the two numbers read as
 * two more template fields — a misread on a screen that spends money.
 *
 * 🔴 Emits `[]` until every field is valid, an object once it is. That is what makes upstream's
 * ACTION_PARAMETERS_REQUIRED fire on its own (validations.js checks `action_params.length === 0`,
 * and an object has no `.length`), so validations.js needs no edit.
 *
 * 🔴 Normalises AND RE-EMITS on load. useEditableAutomation#generateActionsArray has no branch for
 * our inputType and falls through to `[...params]`, handing us an array. Normalising for display
 * alone is not enough: the parent would still hold the array at save time, and
 * actionQueryGenerator#formatArray would map it to `[undefined]` -> JSON `[null]`, saving the rule
 * with no configuration and no error.
 */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useFunctionGetter } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  findComponentByType,
  buildTemplateParameters,
  replaceTemplateVariables,
} from 'dashboard/helper/templateHelper';
import NextInput from 'dashboard/components-next/input/Input.vue';
import { bodyOnlyFillable } from 'dashboard/helper/whatsappTemplateRules';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: () => null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const DEFAULT_DELAY = '0';
const DEFAULT_SUPPRESS = '24';

const inboxId = ref(null);
const templateId = ref(null);
const processedParams = ref({});
// Kept as strings: Input.vue emits '' when a number field is cleared, and '' must be treated as
// invalid rather than coerced — Number('') === 0 would silently disable suppression.
const delayMinutes = ref(DEFAULT_DELAY);
const suppressHours = ref(DEFAULT_SUPPRESS);

const whatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const allTemplates = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  inboxId
);
// The action fills body parameters only; a template needing a header or button value would go out
// broken, and the server refuses to save it (template_not_fillable). Say why some are hidden.
const templates = computed(() => allTemplates.value.filter(bodyOnlyFillable));
const hiddenCount = computed(
  () => allTemplates.value.length - templates.value.length
);

const inboxOptions = computed(() =>
  whatsAppInboxes.value.map(inbox => ({ id: inbox.id, name: inbox.name }))
);

const selectedInbox = computed({
  get: () => inboxOptions.value.find(o => o.id === inboxId.value) || null,
  set: value => {
    const next = value?.id ?? value ?? null;
    if (next === inboxId.value) return;
    inboxId.value = next;
    // A template belongs to one channel; keeping it across a switch would save a pairing
    // that does not exist.
    templateId.value = null;
    processedParams.value = {};
  },
});

const selectedTemplate = computed(
  () => templates.value.find(tpl => tpl.id === templateId.value) || null
);

const templateBody = computed(
  () =>
    findComponentByType(selectedTemplate.value || {}, COMPONENT_TYPES.BODY)
      ?.text || ''
);

const bodyVariables = computed(() => {
  if (!selectedTemplate.value) return [];
  const built = buildTemplateParameters(selectedTemplate.value, false);
  return Object.keys(built.body || {});
});

// Always rendered with the admin's own values substituted — never raw {{1}}. A digit inside curly
// braces is bidi-neutral and jumps to the wrong end of a Hebrew line, so showing the raw form
// would misrepresent the message the customer receives.
const renderedBody = computed(() =>
  replaceTemplateVariables(templateBody.value, { body: processedParams.value })
);

const isPositiveInt = (value, min) =>
  /^\d+$/.test(String(value ?? '').trim()) && Number(value) >= min;

const delayValid = computed(() => isPositiveInt(delayMinutes.value, 0));
// Minimum 1, not 0: a 0 here is a button that disables duplicate protection from the UI. An admin
// who wants no suppression can disable the rule.
const suppressValid = computed(() => isPositiveInt(suppressHours.value, 1));

const paramsComplete = computed(() =>
  bodyVariables.value.every(key =>
    String(processedParams.value[key] ?? '').trim()
  )
);

const isValid = computed(
  () =>
    !!inboxId.value &&
    !!selectedTemplate.value &&
    paramsComplete.value &&
    delayValid.value &&
    suppressValid.value
);

const delayCaption = computed(() =>
  delayMinutes.value === '0'
    ? t('AUTOMATION.WHATSAPP_TEMPLATE.DELAY_IMMEDIATE')
    : t('AUTOMATION.WHATSAPP_TEMPLATE.DELAY_SCHEDULED', {
        count: delayMinutes.value,
      })
);

const buildPayload = () => {
  const tpl = selectedTemplate.value;
  return {
    inbox_id: inboxId.value,
    delay_minutes: Number(delayMinutes.value),
    suppress_hours: Number(suppressHours.value),
    // Stored at save time so the backend never has to re-render a template body — that would mean
    // duplicating the NAMED vs POSITIONAL parameter handling of two upstream services.
    rendered_body: renderedBody.value,
    template_params: {
      name: tpl.name,
      namespace: tpl.namespace,
      category: tpl.category,
      language: tpl.language,
      processed_params: { body: { ...processedParams.value } },
    },
  };
};

// 🔴 The parent echoes every emit straight back as modelValue (AutomationActionInput ->
// AutomationRuleForm `v-model="automation.actions[i]"`). Re-hydrating that echo rebuilt
// processedParams as a NEW object, which re-fired the emit watcher, which emitted a new payload,
// which came back again: an endless update loop that froze the whole page as soon as the form became
// complete (a template with no variables on selection, or the last variable filled). Remember what we
// last emitted, ignore it when it comes back, and never emit the same payload twice.
let lastEmitted = null;

const hydrate = value => {
  if (lastEmitted !== null && JSON.stringify(value ?? null) === lastEmitted)
    return;

  const config = Array.isArray(value) ? value[0] : value;
  if (!config) {
    // The parent emptied this action (e.g. the row above was removed and this slot was reused):
    // show it empty too, instead of a filled form over an empty value.
    lastEmitted = JSON.stringify(value ?? null);
    templateId.value = null;
    processedParams.value = {};
    return;
  }
  // An external value (e.g. a saved rule, or a sibling action's slot after a removal) becomes the
  // baseline, so the next real change always reaches the parent.
  lastEmitted = JSON.stringify(value);

  inboxId.value = config.inbox_id ?? null;
  delayMinutes.value = String(config.delay_minutes ?? DEFAULT_DELAY);
  suppressHours.value = String(config.suppress_hours ?? DEFAULT_SUPPRESS);
  processedParams.value = {
    ...(config.template_params?.processed_params?.body || {}),
  };

  const name = config.template_params?.name;
  const language = config.template_params?.language;
  const match = templates.value.find(
    tpl => tpl.name === name && tpl.language === language
  );
  templateId.value = match ? match.id : null;
};

// Preselect when there is exactly one channel — and emit it, so the admin never sees a filled
// field that was not actually saved.
watch(
  inboxOptions,
  options => {
    if (!inboxId.value && options.length === 1)
      selectedInbox.value = options[0];
  },
  { immediate: true }
);

watch(
  [
    inboxId,
    templateId,
    processedParams,
    delayMinutes,
    suppressHours,
    templates,
  ],
  () => {
    const next = isValid.value ? buildPayload() : [];
    const serialized = JSON.stringify(next);
    if (serialized === lastEmitted) return;
    lastEmitted = serialized;
    emit('update:modelValue', next);
  },
  { deep: true }
);

// 🔴 Declared AFTER the emit watcher on purpose: its immediate first run (loading a saved rule) must
// trigger the emit watcher, so the parent receives the normalised object. Declared before, the load
// emitted nothing, the form kept the array generateActionsArray built, and saving the rule untouched
// sent [null] (actionQueryGenerator#formatArray) -> the backend refused it with missing_params.
watch(() => props.modelValue, hydrate, { immediate: true, deep: true });
</script>

<template>
  <div class="flex flex-col gap-3">
    <p v-if="!inboxOptions.length" class="text-sm text-n-slate-11">
      {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.NO_INBOX') }}
    </p>

    <template v-else>
      <p class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.GROUP_WHAT') }}
      </p>

      <div class="flex flex-col gap-1">
        <label class="text-heading-3 text-n-slate-12">
          {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.CHANNEL') }}
        </label>
        <SingleSelect
          v-model="selectedInbox"
          :options="inboxOptions"
          disable-search
          disable-deselect
        />
      </div>

      <div v-if="inboxId" class="flex flex-col gap-1">
        <label class="text-heading-3 text-n-slate-12">
          {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.TEMPLATE') }}
        </label>
        <!-- Own list rather than SingleSelect: its search icon is positioned with left-2/pl-8,
             which sits on top of the text in RTL. -->
        <p v-if="hiddenCount" class="text-xs text-n-slate-11">
          {{ t('WA_TEMPLATES.AUTOMATION_HIDDEN') }}
        </p>
        <div
          class="flex flex-col overflow-y-auto rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong max-h-64"
        >
          <button
            v-for="tpl in templates"
            :key="tpl.id"
            type="button"
            class="flex flex-col gap-0.5 p-2.5 text-start hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :class="{ 'bg-n-alpha-2': tpl.id === templateId }"
            @click="templateId = tpl.id"
          >
            <span
              dir="auto"
              class="text-sm font-medium text-n-slate-12 font-inter"
            >
              {{ tpl.name }}
            </span>
            <span dir="auto" class="text-sm text-n-slate-11 line-clamp-2">
              {{ findComponentByType(tpl, 'BODY')?.text }}
            </span>
          </button>
        </div>
      </div>

      <div v-if="bodyVariables.length" class="flex flex-col gap-2">
        <label class="text-heading-3 text-n-slate-12">
          {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.FIELDS') }}
        </label>
        <div
          v-for="(key, index) in bodyVariables"
          :key="key"
          class="flex items-center gap-2"
        >
          <span class="text-sm text-n-slate-11 shrink-0">
            {{
              $t('AUTOMATION.WHATSAPP_TEMPLATE.FIELD_LABEL', {
                index: index + 1,
              })
            }}
          </span>
          <div class="flex-1 min-w-0">
            <NextInput v-model="processedParams[key]" size="md" dir="auto" />
          </div>
        </div>
      </div>

      <div
        v-if="selectedTemplate"
        dir="auto"
        class="p-4 rounded-lg bg-n-alpha-black2 whitespace-pre-wrap text-sm text-n-slate-12"
      >
        {{ renderedBody }}
      </div>

      <div class="border-t border-n-weak" aria-hidden="true" />

      <p class="text-label-small text-n-slate-11">
        {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.GROUP_WHEN') }}
      </p>

      <div class="flex flex-col gap-3 sm:flex-row">
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <label class="text-heading-3 text-n-slate-12">
            {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.DELAY') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="flex-1 min-w-0">
              <!-- Wrapped in our own div: Input.vue has no inheritAttrs:false and also
                   v-binds $attrs onto the inner <input>, so any class/dir passed here would be
                   applied twice — including to the Hebrew label. -->
              <NextInput
                v-model="delayMinutes"
                type="number"
                size="md"
                inputmode="numeric"
              />
            </div>
            <span class="text-sm text-n-slate-11 shrink-0">
              {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.MINUTES') }}
            </span>
          </div>
          <p
            dir="auto"
            class="text-label-small"
            :class="delayValid ? 'text-n-slate-11' : 'text-n-ruby-11'"
          >
            {{
              delayValid
                ? delayCaption
                : $t('AUTOMATION.WHATSAPP_TEMPLATE.DELAY_ERROR')
            }}
          </p>
        </div>

        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <label class="text-heading-3 text-n-slate-12">
            {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.SUPPRESS') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="flex-1 min-w-0">
              <NextInput
                v-model="suppressHours"
                type="number"
                size="md"
                inputmode="numeric"
              />
            </div>
            <span class="text-sm text-n-slate-11 shrink-0">
              {{ $t('AUTOMATION.WHATSAPP_TEMPLATE.HOURS') }}
            </span>
          </div>
          <p
            dir="auto"
            class="text-label-small"
            :class="suppressValid ? 'text-n-slate-11' : 'text-n-ruby-11'"
          >
            {{
              suppressValid
                ? $t('AUTOMATION.WHATSAPP_TEMPLATE.SUPPRESS_HINT', {
                    count: suppressHours,
                  })
                : $t('AUTOMATION.WHATSAPP_TEMPLATE.SUPPRESS_ERROR')
            }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
